import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faEllipsisVertical,
    faKey,
    faUsers,
} from "@fortawesome/pro-regular-svg-icons";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "~/components/ui/table";
import { Button } from "~/components/ui/button";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { TimeAgoDisplay } from "~/components/DateDisplay";
import { Emdash } from "~/components/Punctuation";
import { OauthClientStatusBadge } from "../OauthClientStatusBadge";
import { CopyButton } from "../CopyButton";
import { ManagePermissionTooltip } from "../ManagePermissionTooltip";
import { OauthClientListItem } from "../../types";
import {
    formatShortDate,
    toDateString,
    truncateClientId,
} from "../../lib/format";

// // // //

/**
 * OauthClientTable
 * Renders the list of OAuth clients with per-row actions (View, Revoke).
 */
export function OauthClientTable(props: {
    clients: OauthClientListItem[];
    onView: (client: OauthClientListItem) => void;
    onRevoke: (client: OauthClientListItem) => void;
}) {
    const { clients, onView, onRevoke } = props;

    const headClassName =
        "h-auto whitespace-nowrap py-2.5 pl-0 pr-4 text-xs font-medium uppercase tracking-wide text-muted-foreground first:pl-4";
    const cellClassName = "py-3.5 pl-0 pr-4 align-middle first:pl-4";

    return (
        <div className="overflow-x-auto">
            <Table className="min-w-[840px] border-collapse">
                <TableHeader>
                    <TableRow className="border-b hover:bg-transparent">
                        <TableHead className={headClassName}>Client</TableHead>
                        <TableHead className={headClassName}>
                            Client ID
                        </TableHead>
                        <TableHead className={headClassName}>Status</TableHead>
                        <TableHead className={`${headClassName} text-center`}>
                            Credentials
                        </TableHead>
                        <TableHead className={headClassName}>
                            Last used
                        </TableHead>
                        <TableHead className={headClassName}>Created</TableHead>
                        <TableHead className={`${headClassName} w-10`} />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {clients.map((client) => (
                        <TableRow
                            key={client.id}
                            className="group border-b transition-colors last:border-0 hover:bg-muted/40"
                        >
                            <TableCell className={cellClassName}>
                                <div className="flex items-center gap-3">
                                    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                                        <FontAwesomeIcon
                                            icon={faKey}
                                            className="size-4"
                                            aria-hidden="true"
                                        />
                                    </div>
                                    <div className="min-w-0 max-w-[280px]">
                                        <button
                                            type="button"
                                            className="block max-w-full truncate font-medium text-foreground outline-none hover:text-primary hover:underline focus-visible:text-primary focus-visible:underline"
                                            onClick={() => onView(client)}
                                        >
                                            {client.name}
                                        </button>
                                        <p className="truncate text-xs text-muted-foreground">
                                            {client.description ?? <Emdash />}
                                        </p>
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell className={cellClassName}>
                                <CopyButton
                                    value={client.clientId}
                                    label={truncateClientId(client.clientId)}
                                    ariaLabel="Copy client ID"
                                />
                            </TableCell>
                            <TableCell className={cellClassName}>
                                <OauthClientStatusBadge
                                    status={client.status}
                                />
                            </TableCell>
                            <TableCell className={cellClassName}>
                                <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                                    <span
                                        className="inline-flex items-center gap-1 tabular-nums"
                                        title="Secrets"
                                    >
                                        <FontAwesomeIcon
                                            icon={faKey}
                                            className="size-3.5"
                                            aria-hidden="true"
                                        />
                                        {client.secretCount}
                                    </span>
                                    <span
                                        className="inline-flex items-center gap-1 tabular-nums"
                                        title="Sessions"
                                    >
                                        <FontAwesomeIcon
                                            icon={faUsers}
                                            className="size-3.5"
                                            aria-hidden="true"
                                        />
                                        {client.activeSessionCount}
                                    </span>
                                </div>
                            </TableCell>
                            <TableCell
                                className={`${cellClassName} whitespace-nowrap text-muted-foreground`}
                            >
                                {client.lastUsedAt ? (
                                    <TimeAgoDisplay
                                        value={toDateString(client.lastUsedAt)}
                                    />
                                ) : (
                                    "Never used"
                                )}
                            </TableCell>
                            <TableCell
                                className={`${cellClassName} whitespace-nowrap`}
                            >
                                <div className="text-foreground">
                                    {formatShortDate(client.createdAt)}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {client.ownerEmail ?? <Emdash />}
                                </div>
                            </TableCell>
                            <TableCell className={cellClassName}>
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="size-8 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                                            aria-label={`Actions for ${client.name}`}
                                        >
                                            <FontAwesomeIcon
                                                icon={faEllipsisVertical}
                                            />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                        <DropdownMenuItem
                                            onClick={() => onView(client)}
                                        >
                                            View details
                                        </DropdownMenuItem>
                                        <ManagePermissionTooltip
                                            canManage={client.canManage}
                                            placement="left"
                                            className="flex"
                                        >
                                            <DropdownMenuItem
                                                disabled={
                                                    client.status ===
                                                        "revoked" ||
                                                    !client.canManage
                                                }
                                                className="w-full text-destructive focus:text-destructive"
                                                onClick={() => onRevoke(client)}
                                            >
                                                Revoke client
                                            </DropdownMenuItem>
                                        </ManagePermissionTooltip>
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </div>
    );
}
